import React from 'react';
import { Link } from 'react-router-dom';
import type { PassportCollectionDefinition } from '../../services/api/passport';

type Props = {
  collections: PassportCollectionDefinition[];
  onOpen?: (slug: string) => void;
};

export const PassportCollectionsStrip: React.FC<Props> = ({ collections, onOpen }) => {
  if (collections.length === 0) return null;

  const ranked = [...collections].sort((a, b) => {
    const ap = a.progress?.percent_complete ?? 0;
    const bp = b.progress?.percent_complete ?? 0;
    const aDone = a.progress?.status === 'completed' ? 1 : 0;
    const bDone = b.progress?.status === 'completed' ? 1 : 0;
    if (aDone !== bDone) return bDone - aDone;
    return bp - ap;
  });

  return (
    <section className="mb-10" aria-labelledby="passport-collections-heading">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h2 id="passport-collections-heading" className="text-lg font-semibold text-white">
            Collections
          </h2>
          <p className="mt-1 text-sm text-zinc-500">Progress worth returning for.</p>
        </div>
        <Link
          to="/passport/collections"
          className="text-sm text-zinc-400 hover:text-white underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60"
        >
          View all
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ranked.slice(0, 4).map((c) => {
          const pct = Math.max(0, Math.min(100, c.progress?.percent_complete ?? 0));
          const done = c.progress?.status === 'completed' || pct >= 100;
          return (
            <Link
              key={c.public_id}
              to="/passport/collections"
              onClick={() => onOpen?.(c.slug)}
              className={`rounded-2xl border p-4 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60 ${
                done
                  ? 'border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] to-[#0c0c0c]'
                  : 'border-white/[0.08] bg-[#0c0c0c] hover:border-white/[0.14]'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {done ? (
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/90">
                      Complete
                    </p>
                  ) : (
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                      {c.collection_type.replace(/_/g, ' ')}
                    </p>
                  )}
                  <p className="mt-1 text-sm font-semibold text-white truncate">{c.title}</p>
                  {c.subtitle ? (
                    <p className="mt-0.5 text-xs text-zinc-500 line-clamp-1">{c.subtitle}</p>
                  ) : null}
                </div>
                <p className="shrink-0 text-sm font-medium tabular-nums text-zinc-300">{pct}%</p>
              </div>
              <div
                className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/[0.06]"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${c.title} ${pct}% complete`}
              >
                <div
                  className={`h-full rounded-full ${done ? 'bg-amber-400/80' : 'bg-zinc-300/70'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default PassportCollectionsStrip;
